export const DEFAULT_STATISTIQUES_IMAGE = "/img/cover/cover_statistiques.webp";

export const EMPTY_STATS = {
  totalCompetitions: 0,
  completedCompetitions: 0,
  upcomingCompetitions: 0,
  competitionsWithResults: 0,
  totalFights: 0,
  wins: 0,
  losses: 0,
  draws: 0,
  forfeits: 0,
  disqualifications: 0,
  winRate: 0,
  lossRate: 0,
  drawRate: 0,
  podiums: 0,
  firstPlaces: 0,
  secondPlaces: 0,
  thirdPlaces: 0,
  podiumRate: 0,
  averageFightsPerCompetition: 0,
  currentWinStreak: 0,
  bestWinStreak: 0,
  currentLossStreak: 0,
  bestLossStreak: 0,
};

export const RANKS = [
  "1èr(e)",
  "2ème",
  "3ème",
  "4ème",
  "5ème",
  "7ème",
  "9ème",
  "N.C.",
];

export function safeDate(value) {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function todayAtMidnight() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
}

export function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * Saison sportive judo :
 * - du 1er août au 31 juillet
 * - exemple : 01/08/2025 → 31/07/2026 = saison 2025-2026
 */
export function getSportSeasonStartYear(value = new Date()) {
  const date = safeDate(value);
  if (!date) return null;

  const year = date.getFullYear();
  const month = date.getMonth(); // janvier = 0, août = 7

  return month >= 7 ? year : year - 1;
}

export function getSportSeasonLabel(value = new Date()) {
  const startYear = getSportSeasonStartYear(value);

  if (!startYear) return "";

  return `${startYear}-${startYear + 1}`;
}

export function getCurrentSportSeasonLabel() {
  return getSportSeasonLabel(new Date());
}

export function getSportSeasonRangeLabel(seasonLabel) {
  if (!seasonLabel || !seasonLabel.includes("-")) {
    return "";
  }

  const [startYear, endYear] = seasonLabel.split("-");

  return `01/08/${startYear} → 31/07/${endYear}`;
}

export const DEFAULT_STATISTIQUES_FILTERS = {
  search: "",
  season: "all",
  status: "all",
  sort: "dateDesc",
};

export function resolveSeasonFilterValue(seasonFilter) {
  if (seasonFilter === "current") {
    return getCurrentSportSeasonLabel();
  }

  return seasonFilter;
}

export function getStatistiqueEventKey(event, index = "") {
  const adherentId =
    event?.AdherentEvent?.adherent_id || event?.adherent_id || null;

  const eventId =
    event?.AdherentEvent?.event_id || event?.event_id || event?.id || null;

  if (adherentId && eventId) {
    return `stats-${adherentId}-${eventId}`;
  }

  if (event?.id) {
    return `stats-event-${event.id}-${index}`;
  }

  return `stats-fallback-${index}`;
}

export function dedupeStatistiqueEvents(events = []) {
  const seen = new Set();

  return events.filter((event, index) => {
    const key = getStatistiqueEventKey(event, index);

    if (seen.has(key)) {
      console.warn(
        "[StatistiquesJudokaPage] Événement dupliqué ignoré :",
        key,
        event,
      );
      return false;
    }

    seen.add(key);
    return true;
  });
}

export function formatStatDate(value) {
  const date = safeDate(value);
  if (!date) return "Date non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatShortDate(value) {
  const date = safeDate(value);
  if (!date) return "--/--/----";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export function getEventSeason(event) {
  return getSportSeasonLabel(event?.start);
}

export function isUpcomingEvent(event) {
  const date = safeDate(event?.start);
  if (!date) return false;

  return date >= todayAtMidnight();
}

export function isCompletedEvent(event) {
  const date = safeDate(event?.start);
  if (!date) return false;

  return date < todayAtMidnight();
}

export function hasResult(event) {
  return Boolean(
    event?.AdherentEvent?.resultats ||
      event?.AdherentEvent?.classement ||
      event?.AdherentEvent?.observations,
  );
}

export function parseFightResults(resultats = "") {
  return String(resultats || "")
    .split("")
    .filter(Boolean);
}

export function getRankGroup(classement) {
  const value = normalizeText(classement).replace(/\s/g, "");

  if (value.startsWith("1")) return "first";
  if (value.startsWith("2")) return "second";
  if (value.startsWith("3")) return "third";

  return "other";
}

export function isPodium(classement) {
  return ["first", "second", "third"].includes(getRankGroup(classement));
}

export function sortStatistiqueEvents(events = [], sort = "dateDesc") {
  const sorted = [...events];

  if (sort === "dateAsc") {
    return sorted.sort((a, b) => {
      const dateA = safeDate(a?.start)?.getTime() || 0;
      const dateB = safeDate(b?.start)?.getTime() || 0;
      return dateA - dateB;
    });
  }

  if (sort === "titleAsc") {
    return sorted.sort((a, b) =>
      String(a?.titre || "").localeCompare(String(b?.titre || ""), "fr"),
    );
  }

  if (sort === "withResultsFirst") {
    return sorted.sort((a, b) => Number(hasResult(b)) - Number(hasResult(a)));
  }

  return sorted.sort((a, b) => {
    const dateA = safeDate(a?.start)?.getTime() || 0;
    const dateB = safeDate(b?.start)?.getTime() || 0;
    return dateB - dateA;
  });
}

export function filterStatistiqueEvents(
  events = [],
  filters = DEFAULT_STATISTIQUES_FILTERS,
) {
  const search = normalizeText(filters.search);
  const resolvedSeason = resolveSeasonFilterValue(filters.season);

  const filtered = events.filter((event) => {
    const season = getEventSeason(event);

    const searchableContent = normalizeText(
      [
        event?.titre,
        event?.description,
        event?.lieu_event,
        event?.type_event,
        event?.level_event,
        event?.AdherentEvent?.resultats,
        event?.AdherentEvent?.classement,
        event?.AdherentEvent?.observations,
        season,
      ].join(" "),
    );

    const matchesSearch = !search || searchableContent.includes(search);

    const matchesSeason =
      filters.season === "all" || season === resolvedSeason;

    const matchesStatus =
      filters.status === "all" ||
      (filters.status === "completed" && isCompletedEvent(event)) ||
      (filters.status === "upcoming" && isUpcomingEvent(event)) ||
      (filters.status === "withResults" && hasResult(event)) ||
      (filters.status === "withoutResults" && !hasResult(event)) ||
      (filters.status === "podium" &&
        isPodium(event?.AdherentEvent?.classement));

    return matchesSearch && matchesSeason && matchesStatus;
  });

  return sortStatistiqueEvents(filtered, filters.sort);
}

export function countActiveStatistiquesFilters(
  filters = DEFAULT_STATISTIQUES_FILTERS,
) {
  return Object.entries(filters).reduce((count, [key, value]) => {
    if (key === "season" && value === "all") return count;
    if (key === "status" && value === "all") return count;
    if (key === "sort" && value === "dateDesc") return count;
    if (!value) return count;

    return count + 1;
  }, 0);
}

export function getAvailableSeasons(events = []) {
  return Array.from(
    new Set(events.map((event) => getEventSeason(event)).filter(Boolean)),
  ).sort((a, b) => {
    const startA = Number(String(a).split("-")[0]);
    const startB = Number(String(b).split("-")[0]);

    return startB - startA;
  });
}

export function getFightCounters(events = []) {
  let totalFights = 0;
  let wins = 0;
  let losses = 0;
  let draws = 0;
  let forfeits = 0;
  let disqualifications = 0;

  const fightSequence = [];

  const chronologicalEvents = sortStatistiqueEvents(events, "dateAsc");

  chronologicalEvents.forEach((event) => {
    const results = parseFightResults(event?.AdherentEvent?.resultats);

    results.forEach((result) => {
      totalFights += 1;

      if (result === "✅") {
        wins += 1;
        fightSequence.push("win");
        return;
      }

      if (result === "N") {
        draws += 1;
        fightSequence.push("draw");
        return;
      }

      if (result === "F") {
        forfeits += 1;
        losses += 1;
        fightSequence.push("loss");
        return;
      }

      if (result === "D") {
        disqualifications += 1;
        losses += 1;
        fightSequence.push("loss");
        return;
      }

      if (result === "❌") {
        losses += 1;
        fightSequence.push("loss");
      }
    });
  });

  return {
    totalFights,
    wins,
    losses,
    draws,
    forfeits,
    disqualifications,
    fightSequence,
  };
}

export function getStreakStats(fightSequence = []) {
  let currentWinStreak = 0;
  let currentLossStreak = 0;
  let bestWinStreak = 0;
  let bestLossStreak = 0;

  let runningWinStreak = 0;
  let runningLossStreak = 0;

  fightSequence.forEach((result) => {
    if (result === "win") {
      runningWinStreak += 1;
      runningLossStreak = 0;
      bestWinStreak = Math.max(bestWinStreak, runningWinStreak);
      return;
    }

    if (result === "loss") {
      runningLossStreak += 1;
      runningWinStreak = 0;
      bestLossStreak = Math.max(bestLossStreak, runningLossStreak);
      return;
    }

    runningWinStreak = 0;
    runningLossStreak = 0;
  });

  for (let index = fightSequence.length - 1; index >= 0; index -= 1) {
    const result = fightSequence[index];

    if (result === "draw") {
      continue;
    }

    if (result === "win" && currentLossStreak === 0) {
      currentWinStreak += 1;
      continue;
    }

    if (result === "loss" && currentWinStreak === 0) {
      currentLossStreak += 1;
      continue;
    }

    break;
  }

  return {
    currentWinStreak,
    currentLossStreak,
    bestWinStreak,
    bestLossStreak,
  };
}

export function percent(value, total) {
  if (!total) return 0;
  return Math.round((value / total) * 100);
}

export function getRankingCounters(events = []) {
  const rankings = RANKS.reduce((acc, rank) => {
    acc[rank] = 0;
    return acc;
  }, {});

  events.forEach((event) => {
    const classement = event?.AdherentEvent?.classement || "N.C.";

    if (rankings[classement] !== undefined) {
      rankings[classement] += 1;
    } else {
      rankings["N.C."] += 1;
    }
  });

  return rankings;
}

export function buildSeasonalData(events = []) {
  const grouped = {};

  events.forEach((event) => {
    const season = getEventSeason(event) || "Non daté";

    if (!grouped[season]) {
      grouped[season] = {
        season,
        competitions: 0,
        combats: 0,
        victoires: 0,
        podiums: 0,
      };
    }

    grouped[season].competitions += 1;

    const results = parseFightResults(event?.AdherentEvent?.resultats);

    results.forEach((result) => {
      grouped[season].combats += 1;

      if (result === "✅") {
        grouped[season].victoires += 1;
      }
    });

    if (isPodium(event?.AdherentEvent?.classement)) {
      grouped[season].podiums += 1;
    }
  });

  return Object.values(grouped).sort((a, b) => {
    const startA = Number(String(a.season).split("-")[0]);
    const startB = Number(String(b.season).split("-")[0]);

    return startA - startB;
  });
}

export function buildRecentResults(events = []) {
  return sortStatistiqueEvents(
    events.filter((event) => hasResult(event)),
    "dateDesc",
  ).slice(0, 6);
}

export function buildStatistiquesData(events = []) {
  const completedCompetitions = events.filter(isCompletedEvent).length;
  const upcomingCompetitions = events.filter(isUpcomingEvent).length;
  const competitionsWithResults = events.filter(hasResult).length;

  const fightCounters = getFightCounters(events);
  const streaks = getStreakStats(fightCounters.fightSequence);
  const rankings = getRankingCounters(events);

  const firstPlaces = rankings["1èr(e)"] || 0;
  const secondPlaces = rankings["2ème"] || 0;
  const thirdPlaces = rankings["3ème"] || 0;
  const podiums = firstPlaces + secondPlaces + thirdPlaces;

  const averageFightsPerCompetition =
    competitionsWithResults > 0
      ? Number((fightCounters.totalFights / competitionsWithResults).toFixed(1))
      : 0;

  const stats = {
    ...EMPTY_STATS,
    totalCompetitions: events.length,
    completedCompetitions,
    upcomingCompetitions,
    competitionsWithResults,
    ...fightCounters,
    ...streaks,
    winRate: percent(fightCounters.wins, fightCounters.totalFights),
    lossRate: percent(fightCounters.losses, fightCounters.totalFights),
    drawRate: percent(fightCounters.draws, fightCounters.totalFights),
    podiums,
    firstPlaces,
    secondPlaces,
    thirdPlaces,
    podiumRate: percent(podiums, completedCompetitions),
    averageFightsPerCompetition,
  };

  const chartData = {
    combatResults: [
      {
        name: "Gagnés",
        value: fightCounters.wins,
        color: "#22c55e",
      },
      {
        name: "Perdus",
        value: fightCounters.losses,
        color: "#ef4444",
      },
      {
        name: "Nuls",
        value: fightCounters.draws,
        color: "#94a3b8",
      },
    ],
    classement: Object.entries(rankings)
      .filter(([, count]) => count > 0)
      .map(([name, value]) => ({
        name,
        value,
      })),
    seasonal: buildSeasonalData(events),
    recentResults: buildRecentResults(events),
  };

  return {
    stats,
    chartData,
  };
}